/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { ProductReward, Product } from "@lemmonhq/types";
import { Loading as LoadingComponent } from "@lemmonhq/uikit";
import { api } from "@app/data/index";

export type ProductRewardEditorProps = {
  value: ProductReward;
  onChange: (reward: ProductReward) => void;
};

export const ProductRewardEditor = defineComponent<ProductRewardEditorProps>({
  name: "ProductRewardEditor",
  props: ["value", "onChange"],
  setup(props) {
    const products = ref<Product[]>([]);
    const loading = ref(false);

    const loadProducts = async () => {
      if (loading.value || products.value.length > 0) return;
      try {
        loading.value = true;
        products.value = await api.getProducts();
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadProducts();
    });

    const updateField = (field: keyof ProductReward, value: any) => {
      props.onChange({
        ...props.value,
        [field]: value,
      });
    };

    return () => (
      <div class="card bg-gradient-to-r from-success/10 to-success/5 border border-success/20">
        <div class="card-body p-4">
          <h4 class="card-title text-accent text-lg flex items-center gap-2">
            <div class="w-3 h-3 bg-success rounded-full animate-pulse"></div>
            Product Reward
          </h4>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Reward Product</span>
              </label>

              {loading.value ? (
                <div class="flex justify-center py-4">
                  <LoadingComponent />
                </div>
              ) : (
                <select
                  value={props.value.product_id || ""}
                  onChange={(e) => {
                    const value = (e.target as HTMLSelectElement).value;
                    updateField(
                      "product_id",
                      value ? parseInt(value) : undefined,
                    );
                  }}
                  class="select select-bordered focus:select-success"
                >
                  <option value="">Same as purchased product</option>
                  {products.value.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              )}

              <label class="label">
                <span class="label-text-alt">
                  Leave empty to give the same product that triggered the
                  promotion
                </span>
              </label>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Quantity</span>
              </label>
              <input
                type="number"
                value={props.value.quantity}
                onInput={(e) =>
                  updateField(
                    "quantity",
                    parseInt((e.target as HTMLInputElement).value) || 1,
                  )
                }
                class="input input-bordered focus:input-success"
                min="1"
                placeholder="1"
              />
              <label class="label">
                <span class="label-text-alt">
                  Number of free products to give
                </span>
              </label>
            </div>
          </div>

          <div class="alert alert-success/20 border-success/30 mt-4">
            <div class="flex items-center gap-2">
              <div class="text-success text-xl">🎁</div>
              <div>
                <div class="font-medium text-success">Free Product Reward</div>
                <div class="text-sm text-success/80">
                  Customer will receive {props.value.quantity || 1} free{" "}
                  {props.value.product_id
                    ? "selected product"
                    : "qualifying product"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
